'use client';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState
} from 'react';
import { IoMdClose } from 'react-icons/io';
import { toast, ToastContainer } from 'react-toastify';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import AgentVariables from '@/components/agentVariables/agentVariables';
import { ChatConversationMessage } from '@/components/chatConversation/chatConversation';
import { Loader } from '@/components/loader/loader';
import LogDetail from '@/components/logs/logDetail/logDetail';
import RenderLogList from '@/components/logs/renderLogList/renderLogList';
import PlaygroundChat from '@/components/playgroundChat/playgroundChat';
import { PlaygroundInfo, PlaygroundType } from '@/components/playgroundHeader/playgroundHeader';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useStreamingParser } from '@/hooks/useStreamingParser';
import { AgentInvokeArgs, AgentInvokeStreamChunk } from '@/types/agent';
import {
  AgentInvokeApiReqUser,
  ChatMessageContentStructured,
  ChatMessageContentTextual,
  ChatMsgHistory,
  ChatMsgHistoryItem,
  ChatMsgTypeEnum,
  ChatRoleEnum,
  CompletionResponseTypeEnum
} from '@/types/chat';
import type { PlaygroundLog } from '@/types/logs';

import styles from './page.module.scss';

type Props = {
  an: string;
};

export default function AgentAidp( props: Props ) {
  const { sessionContext } = useAuthContext();
  const id = useId();

  const {
    an: propAgentId
  } = props;

  const [ sAgentId ] = useState<string>( propAgentId || 'unknown-agent' );
  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgHistory>( [] );
  const [ sChatUsers, setSChatUsers ] = useState<AgentInvokeApiReqUser[]>();
  const [ sContext, setSContext ] = useState<string | undefined>();
  const [ sCorrelationId, setSCorrelationId ] = useState<string | undefined>();
  const [ sDebug, _setSDebug ] = useState<boolean>( false );
  const [ sInput, setSInput ] = useState<string>( '' );
  const [ sInputs, setSInputs ] = useState<string | undefined>();
  const [ sIsLoading, setSIsLoading ] = useState<boolean>( true );
  const [ sIsWaiting, setSIsWaiting ] = useState<boolean>( false );

  const [ sLogs, setSLogs ] = useState<PlaygroundLog[]>( [] );
  const [ sActiveLog, setSActiveLog ] = useState<PlaygroundLog | undefined>( undefined );

  const abortControllerRef = useRef<AbortController | null>( null );
  const correlationIdRef = useRef<string | undefined>( undefined );
  const currentMessageIdRef = useRef<string | undefined>( undefined );
  const inputRef = useRef<string>( '' );
  const messagesEndRef = useRef<null | HTMLDivElement>( null );

  useEffect( () => {
    setSIsLoading( false );
  }, [ props ] );

  useEffect( () => {
    inputRef.current = sInput;
  }, [ sInput ] );

  useEffect( () => {
    const chatUsers = [
      {
        displayName: 'User',
        id: sessionContext!.userId!,
        type: ChatRoleEnum.USER
      },
      {
        displayName: sAgentId,
        id: sAgentId,
        type: ChatRoleEnum.ASSISTANT
      }
    ];

    setSChatUsers( chatUsers );
  }, [ sAgentId, sessionContext ] );

  const parseAgentChunk = useCallback( ( chunk: unknown ) => {
    const typedChunk = chunk as AgentInvokeStreamChunk;

    // Store correlationId synchronously in ref so it's available when loadLogs runs
    if( typedChunk.correlation_id && !sCorrelationId ) {
      correlationIdRef.current = typedChunk.correlation_id;
      setSCorrelationId( typedChunk.correlation_id );
    }

    // Handle errors
    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_ERROR ) {
      return { error: typedChunk.errorMessage || typedChunk.type, shouldUpdate: false };
    }

    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_FAILURE ) {
      return { error: typedChunk.failureMessage || typedChunk.type, shouldUpdate: false };
    }

    // Process result chunks
    if( typedChunk.type === CompletionResponseTypeEnum.AI_DATA_PLATFORM_RESULT && typedChunk.messages && typedChunk.messages.length > 0 ) {
      const msg = typedChunk.messages[0];

      if( !msg.content ) {
        return { shouldUpdate: false };
      }

      // Use the message ID, if specified, to know if this should replace an existing message
      // or a new one should get created
      currentMessageIdRef.current = msg.message_id || typedChunk.response_id;

      // AIDP sends full accumulated content in each chunk (not deltas)
      // So we use a special marker to indicate this is a full replacement
      // The marker will be stripped in handleUpdateAgentMessage
      return {
        content: '__FULL_REPLACE__' + JSON.stringify( msg.content ),
        shouldUpdate: true
      };
    }

    return { shouldUpdate: false };
  }, [ sCorrelationId ] );

  const handleUpdateAgentMessage = useCallback( ( accumulatedContent: string ) => {
    // AIDP chunks contain full content (not deltas), so extract the latest
    // by taking the last __FULL_REPLACE__ marker
    const fullReplaceMarker = '__FULL_REPLACE__';
    const lastMarkerIndex = accumulatedContent.lastIndexOf( fullReplaceMarker );
    const contentJson = lastMarkerIndex >= 0
      ? accumulatedContent.substring( lastMarkerIndex + fullReplaceMarker.length )
      : accumulatedContent;

    const content = JSON.parse( contentJson );

    const incomingMessage: ChatMsgHistoryItem = {
      authorId: sAgentId,
      content,
      createdAt: DateTime.now(),
      messageId: currentMessageIdRef.current || crypto.randomUUID(),
      role: ChatRoleEnum.ASSISTANT,
      updatedAt: DateTime.now()
    };


    setSChatMsgHistory( prevHistory => {
      const updatedHistory = [ ...prevHistory ];
      const existingMessageIndex = prevHistory.findLastIndex( msg => msg.messageId === incomingMessage.messageId );

      if( existingMessageIndex === -1 ) {
        // novel message - add new message
        updatedHistory.push( incomingMessage );
      } else {
        // Subsequent chunks - update existing message
        updatedHistory[existingMessageIndex] = incomingMessage;
      }

      return updatedHistory;
    } );



    setTimeout( () => {
      messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
    }, 100 );
  }, [ sAgentId ] );

  const { processStream, abort } = useStreamingParser( {
    parseChunk: parseAgentChunk,
    onUpdateMessage: handleUpdateAgentMessage,
    onError: ( error ) => {
      console.error( error.message );
      toast.error( error.message );
      loadLogs();
    },
    onAbort: () => {
      setSIsWaiting( false );
      if ( correlationIdRef.current ) {
        loadLogs();
      }
    },
    onComplete: () => {
      loadLogs();
    }
  } );

  const handleStop = useCallback( () => {
    if( abortControllerRef.current ) {
      abortControllerRef.current.abort();
    }
    abort();
  }, [ abort ] );

  const loadLogs = useCallback( async() => {
    const cid = correlationIdRef.current;
    const [ actionResult, modelResult, requestResult ] = await Promise.allSettled( [
      APIGateway.logs.actionLogsGet( sAgentId, 10, 0, undefined, cid ),
      APIGateway.logs.modelLogsGet( sAgentId, 10, 0, undefined, cid ),
      APIGateway.logs.requestLogsGet( sAgentId, 10, 0, undefined, cid )
    ] );

    const logs: PlaygroundLog[] = [
      ...( actionResult.status === 'fulfilled' ? actionResult.value.agent_actions.map( l => ( { ...l, _logType: 'action' as const } ) ) : [] ),
      ...( modelResult.status === 'fulfilled' ? modelResult.value.model_invocations.map( l => ( { ...l, _logType: 'model' as const } ) ) : [] ),
      ...( requestResult.status === 'fulfilled' ? requestResult.value.completion_requests.map( l => ( { ...l, _logType: 'request' as const } ) ) : [] )
    ];

    const sortedArray = logs.sort( ( a, b ) => new Date( a.start_time ).getTime() - new Date( b.start_time ).getTime()
    );
    setSLogs( sortedArray );
  }, [ sAgentId ] );

  const onClickChat = useCallback( async( mode: string ) => {
    setSIsWaiting( true );
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      let compReqMsg: ChatMsgHistoryItem<ChatMessageContentTextual> | ChatMsgHistoryItem<ChatMessageContentStructured> | undefined = undefined;
      if( mode == 'text' ) {
        compReqMsg = {
          authorId: sessionContext!.userId!,
          content: {
            type: ChatMsgTypeEnum.TEXTUAL_CONTENT,
            text: inputRef.current
          },
          createdAt: DateTime.now(),
          messageId: crypto.randomUUID(),
          role: ChatRoleEnum.USER,
          updatedAt: DateTime.now()
        };
      }
      if ( mode == 'structured' ) {
        try {
          const data = JSON.parse( inputRef.current );
          compReqMsg = {
            authorId: sessionContext!.userId!,
            content: {
              type: ChatMsgTypeEnum.STRUCTURED_CONTENT,
              data: data
            },
            createdAt: DateTime.now(),
            messageId: crypto.randomUUID(),
            role: ChatRoleEnum.USER,
            updatedAt: DateTime.now()
          };
        } catch {
          toast.error( 'Input is not valid JSON' );
          setSIsWaiting( false );
          abortControllerRef.current = null;
          return;
        }
      }
      if ( typeof( compReqMsg ) == 'undefined' ) {
        toast.error( 'Invalid content type' );
        setSIsWaiting( false );
        abortControllerRef.current = null;
        return;
      }

      const newChatMsgHistory: ChatMsgHistory = [
        ...sChatMsgHistory,
        compReqMsg
      ];

      setSChatMsgHistory( newChatMsgHistory );

      setTimeout( ()=>{
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      setSInput( '' );

      const typeCompatibleMessages = newChatMsgHistory.filter( msg => {
        return [ 'TextualContent', 'StructuredContent' ].includes( msg.content.type );
      } );

      const args: AgentInvokeArgs = {
        clientId: 'aidp-studio',
        context: {
          values: sContext ? JSON.parse( sContext ) : []
        },
        debug: sDebug,
        messages: typeCompatibleMessages,
        agentId: sAgentId!,
        users: sChatUsers!
      };

      if( sCorrelationId ) {
        args.correlationId = sCorrelationId;
      }

      const stream = await APIGateway.agents.agentInvokeAidpStream( args, abortControllerRef.current.signal );
      await processStream( stream );
    } catch( e ) {
      const error = e as Error;
      // Don't show error toast if the request was aborted by user
      if( error.name !== 'AbortError' ) {
        console.error( error.message );
        toast.error( error.message );
      }
    }

    setSIsWaiting( false );
    abortControllerRef.current = null;
  }, [ sessionContext, sChatMsgHistory, sContext, sDebug, sAgentId, sChatUsers, sCorrelationId, processStream ] );

  const onClickReset = useCallback( () => { setSActiveLog( undefined ); setSLogs( [] ); correlationIdRef.current = undefined; setSCorrelationId( undefined ); setSChatMsgHistory( [] ); }, [] );

  const handleInputChange = useCallback( ( value: string ) => {
    setSInput( value );
  }, [] );

  const handleContextChange = useCallback( ( value: string ) => {
    setSContext( value );
  }, [] );

  const handleInputsChange = useCallback( ( value: string ) => {
    setSInputs( value );
  }, [] );

  const onLogClick = ( index: number ) =>{
    if( sLogs && sLogs[index] ) setSActiveLog( sLogs[index] );
  };
  const onCloseLogView = () => {
    setSActiveLog( undefined );
  };

  const memoizedMessages = useMemo( () => sChatMsgHistory.map( ( msg: ChatMsgHistoryItem ): ChatConversationMessage => {
    if ( msg.content.type === ChatMsgTypeEnum.TEXTUAL_CONTENT ) {
      return { role: msg.role, text: msg.content.text as unknown as string };
    } else {
      return { role: msg.role, json: msg.content as unknown as string };
    }
  } ), [ sChatMsgHistory ] );

  return (
    <div className={ clsx( 'page', 'no_overflow' ) }>
      <ToastContainer />
      {
        sIsLoading
          ? <Loader />
          : (
              <div className={ clsx( 'layout__col', 'overflow_hide', styles.parent_container ) }>
                <div className={ clsx( 'layout__row', styles.playground_type ) }>
                  <PlaygroundType label="Full-Code Agent" />
                </div>
                <div className={ clsx( 'layout__row', styles.playground_info ) }>
                  <PlaygroundInfo
                    name={ sAgentId }
                    onReset={ onClickReset }
                    backHref="/agents"
                  />
                </div>
                <div className={ clsx( 'layout__row', 'overflow_hide', styles.playground_chat ) }>
                  <div className={ clsx( 'layout__col', styles.chat_context ) } data-testid="playground-context-panel">
                    <div data-testid="playground-inputs-section">
                      <AgentVariables
                        label="Inputs"
                        value={ sInputs }
                        onChange={ handleInputsChange }
                      />
                    </div>
                    <div data-testid="playground-context-section">
                      <AgentVariables
                        label="Context"
                        value={ sContext }
                        onChange={ handleContextChange }
                      />
                    </div>
                    <RenderLogList
                      logs = { sLogs }
                      onLogClick = { onLogClick }
                    />
                  </div>
                  <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_convo ) }>
                    {sActiveLog && (
                      <div className={ styles.logView }>
                        <h2>Log Details <a onClick={ onCloseLogView } className={ styles.closebtn }><IoMdClose /></a></h2>

                        <LogDetail
                          log= { sActiveLog }
                        />
                      </div>
                    )}
                    <div className={ clsx( 'layout__col', 'overflow_hide', styles.chat_container ) }>

                      <PlaygroundChat
                        id={ id }
                        input={ sInput }
                        isWaiting={ sIsWaiting }
                        messages={ memoizedMessages }
                        enableStructured= { true }
                        messagesEndRef={ messagesEndRef }
                        onClickChat={ onClickChat }
                        onClickStop={ handleStop }
                        onInputChange={ handleInputChange }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
      }
    </div>
  );
}
